import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef,
  input, OnDestroy, ViewChild, inject,
} from '@angular/core';
import { combineLatest, map, Subscription, Subject, merge, fromEvent } from 'rxjs';
import { TabService } from '../../services/tab.service';
import { LetDirective } from '@ngrx/component';
import { RouterLink } from '@angular/router';
import { debounceTime } from 'rxjs/operators';
import { NgScrollbar } from 'ngx-scrollbar';
import { ButtonIconComponent } from 'src/app/ui-components/button-icon/button-icon.component';

@Component({
  selector: 'alg-tab-bar',
  templateUrl: './tab-bar.component.html',
  styleUrls: [ './tab-bar.component.scss' ],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '(window:resize)': 'resize()',
  },
  imports: [ LetDirective, RouterLink, NgScrollbar, ButtonIconComponent ]
})
export class TabBarComponent implements AfterViewInit, OnDestroy {
  private tabService = inject(TabService);
  private elementRef = inject<ElementRef<HTMLDivElement>>(ElementRef);
  private cd = inject(ChangeDetectorRef);

  @ViewChild(NgScrollbar, { static: false }) scrollbarRef?: NgScrollbar;
  styleClass = input<string>();

  showPrevButton = false;
  showNextButton = false;

  tabs$ = combineLatest([ this.tabService.tabs$, this.tabService.activeTab$ ]).pipe(
    map(([ tabs, active ]) => tabs.map(tab => ({
      label: tab.title,
      routerLink: tab.command,
      id: tab.tag,
      styleClass: tab.tag === active ? 'alg-tab-bar-active' : undefined,
    }))),
  );

  resizeEvent = new Subject<void>();
  resizeObserver = new ResizeObserver(() =>
    this.resizeEvent.next()
  );

  readonly subscriptions = new Subscription();

  resize(): void {
    this.handleArrows();
  }

  ngAfterViewInit(): void {
    if (!this.scrollbarRef) return;
    this.resizeObserver.observe(this.elementRef.nativeElement);
    this.subscriptions.add(
      merge(
        this.resizeEvent.asObservable(),
        fromEvent(this.scrollbarRef.nativeElement, 'scroll'),
        this.tabs$,
      ).pipe(
        debounceTime(50),
      ).subscribe(() =>
        this.handleArrows()
      )
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.resizeObserver.unobserve(this.elementRef.nativeElement);
    this.resizeEvent.complete();
  }

  onChange(id: string): void {
    this.tabService.setActiveTab(id);
  }

  handleArrows(): void {
    if (!this.scrollbarRef) throw new Error('Unexpected: Missed scrollbar');
    const scrollLeft = this.scrollbarRef.nativeElement.scrollLeft;
    const scrollWidth = this.scrollbarRef.nativeElement.scrollWidth;
    const clientWidth = this.scrollbarRef.nativeElement.clientWidth;
    const gap = 5;
    this.showPrevButton = clientWidth !== Math.round(scrollWidth) && scrollLeft > gap;
    this.showNextButton = clientWidth !== Math.round(scrollWidth)
      && (scrollLeft + clientWidth) < (Math.round(scrollWidth) - gap);
    this.cd.detectChanges();
  }

  onPrev(): void {
    if (!this.scrollbarRef) throw new Error('Unexpected: Missed scrollbar');
    const scrollLeft = this.scrollbarRef.nativeElement.scrollLeft;
    const clientWidth = this.scrollbarRef.nativeElement.clientWidth;
    void this.scrollbarRef.scrollTo({
      left: scrollLeft - (clientWidth / 2),
    });
  }

  onNext(): void {
    if (!this.scrollbarRef) throw new Error('Unexpected: Missed scrollbar');
    const scrollLeft = this.scrollbarRef.nativeElement.scrollLeft;
    const clientWidth = this.scrollbarRef.nativeElement.clientWidth;
    void this.scrollbarRef.scrollTo({
      left: scrollLeft + (clientWidth / 2),
    });
  }
}
